import React, { useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu } from "lucide-react";

import { Button } from "components/ui/button";
import { Separator } from "components/ui/separator";
import {
  Sheet,
  SheetClose,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from "components/ui/sheet";
import { cn } from "components/lib/utils";
import { navItems } from "constants/navItems";
import { useAuth } from "context/AuthContext";

/**
 * Hamburger navigation. Replaces the old horizontal link row in the navbar so the
 * header stays a fixed height on phones no matter how many screens get added.
 * The sheet slides in from the right (RTL "start" edge) and lists the entries
 * top-down in the same order they used to appear right-to-left.
 */
export default function AppSidebar() {
  const { user } = useAuth();
  const { pathname } = useLocation();
  const [open, setOpen] = useState(false);

  const items = navItems.filter((item) => !item.protected || user);

  return (
    <Sheet open={open} onOpenChange={setOpen}>
      <SheetTrigger asChild>
        <Button variant="ghost" size="icon" className="shrink-0" aria-label="תפריט">
          <Menu className="h-5 w-5" />
        </Button>
      </SheetTrigger>

      {/* The sheet primitive's built-in close button is pinned to the physical right
          edge, which in RTL is the start side where the title sits — the
          `[&>button:last-child]` override flips it to the left. */}
      <SheetContent
        side="right"
        dir="rtl"
        className="flex w-[80vw] max-w-xs flex-col gap-0 p-0 sm:max-w-sm [&>button:last-child]:left-4 [&>button:last-child]:right-auto"
      >
        <SheetHeader className="px-5 pb-4 pt-[calc(env(safe-area-inset-top)+1.25rem)] text-right sm:text-right">
          <SheetTitle className="text-primary">ניהול חנות</SheetTitle>
        </SheetHeader>

        <Separator />

        <nav className="flex flex-1 flex-col gap-1 overflow-y-auto p-3">
          {items.map((item) => {
            const Icon = item.icon;
            const isActive =
              item.to === "/" ? pathname === "/" : pathname.startsWith(item.to);

            return (
              <SheetClose asChild key={item.to}>
                <Link
                  to={item.to}
                  aria-current={isActive ? "page" : undefined}
                  className={cn(
                    "flex items-center gap-3 rounded-md px-3 py-3 text-base font-medium transition-colors",
                    isActive
                      ? "bg-primary text-primary-foreground"
                      : "text-foreground hover:bg-accent hover:text-accent-foreground"
                  )}
                >
                  <Icon className="h-5 w-5 shrink-0" />
                  <span className="truncate">{item.label}</span>
                </Link>
              </SheetClose>
            );
          })}
        </nav>
      </SheetContent>
    </Sheet>
  );
}
